import { Component } from '@theme/component';
import {
  debounce,
  preloadImage,
} from '@theme/utilities';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A custom element that renders a zoom dialog with a Tikamoon-style horizontal gallery.
 * Uses native scroll for touchpad/wheel/touch, with arrow buttons for click navigation.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The slide elements.
 * @property {HTMLElement} thumbnails - Hidden thumbnails (kept for compatibility).
 * @property {HTMLElement} slider - The scrollable slider container.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());
  #currentIndex = 0;

  connectedCallback() {
    super.connectedCallback();
    const slider = this.refs.slider;
    if (slider) {
      slider.addEventListener('scroll', this.#handleSliderScroll);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const slider = this.refs.slider;
    if (slider) {
      slider.removeEventListener('scroll', this.#handleSliderScroll);
    }
  }

  /**
   * Opens the zoom dialog at a specific image index.
   *
   * @param {number} index - The index of the media to show.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media } = this.refs;
    const slider = this.refs.slider;

    this.#currentIndex = index;
    dialog.showModal();

    // Scroll to the target slide instantly
    if (slider && media[index]) {
      slider.scrollLeft = media[index].offsetLeft;
    }

    this.#updateArrows();
    this.#loadHighResolutionImage(media[index]);
    this.#preloadNearby(index);
  }

  /**
   * Navigate to the previous image via arrow click.
   */
  navigatePrev() {
    const { media } = this.refs;
    if (this.#currentIndex <= 0) return;
    this.#currentIndex--;
    this.#scrollToIndex(this.#currentIndex);
  }

  /**
   * Navigate to the next image via arrow click.
   */
  navigateNext() {
    const { media } = this.refs;
    if (this.#currentIndex >= media.length - 1) return;
    this.#currentIndex++;
    this.#scrollToIndex(this.#currentIndex);
  }

  /**
   * Smooth-scroll the slider to show the slide at the given index.
   * @param {number} index
   */
  #scrollToIndex(index) {
    const { media } = this.refs;
    const slider = this.refs.slider;
    const target = media[index];
    if (!slider || !target) return;

    slider.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
    this.#updateArrows();
    this.#loadHighResolutionImage(target);
    this.#preloadNearby(index);
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }

  /**
   * Debounced handler for native scroll — syncs currentIndex and arrows.
   */
  #handleSliderScroll = debounce(() => {
    const { media } = this.refs;
    const slider = this.refs.slider;
    if (!slider || !media.length) return;

    const scrollLeft = slider.scrollLeft;
    let closestIndex = 0;
    let closestDist = Infinity;

    for (let i = 0; i < media.length; i++) {
      const dist = Math.abs(media[i].offsetLeft - scrollLeft);
      if (dist < closestDist) {
        closestDist = dist;
        closestIndex = i;
      }
    }

    this.#currentIndex = closestIndex;
    this.#updateArrows();
    this.#preloadNearby(closestIndex);
  }, 80);

  /**
   * Show/hide arrows based on current position.
   */
  #updateArrows() {
    const { media } = this.refs;
    const slider = this.refs.slider;
    if (!slider) return;

    const content = slider.parentElement;
    if (!content) return;

    const leftArrow = content.querySelector('.dialog-zoomed-gallery__arrow--left');
    const rightArrow = content.querySelector('.dialog-zoomed-gallery__arrow--right');

    if (leftArrow) {
      leftArrow.style.display = this.#currentIndex <= 0 ? 'none' : 'block';
    }
    if (rightArrow) {
      rightArrow.style.display = this.#currentIndex >= media.length - 1 ? 'none' : 'block';
    }
  }

  /**
   * Preloads high-resolution images for nearby slides.
   * @param {number} index
   */
  #preloadNearby(index) {
    const { media } = this.refs;
    for (let i = Math.max(0, index - 1); i <= Math.min(media.length - 1, index + 3); i++) {
      this.#loadHighResolutionImage(media[i]);
    }
  }

  /**
   * Loads a high-resolution image for a specific media container.
   * @param {HTMLElement} mediaContainer
   */
  #loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer) return;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);

    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Closes the zoom dialog.
   */
  async close() {
    this.closeDialog();
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Handles keyboard events.
   * @param {KeyboardEvent} event
   */
  handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.navigatePrev();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.navigateNext();
    }
  }

  /**
   * Closes the dialog when clicking on the backdrop.
   * @param {PointerEvent} event
   */
  handleBackdropClick(event) {
    if (event.target === this.refs.dialog) {
      this.close();
    }
  }

  // Compatibility methods
  async handleThumbnailClick(index) {
    this.#currentIndex = index;
    this.#scrollToIndex(index);
  }

  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (media[index]) this.#loadHighResolutionImage(media[index]);
  }

  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    this.#currentIndex = index;
    this.#scrollToIndex(index);
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}
